import type {
  HTMLAttributes,
  ReactNode,
  TableHTMLAttributes,
  TdHTMLAttributes,
  ThHTMLAttributes,
} from "react";

import { cx } from "./cx";

/**
 * The shared table primitives (PHP-3871). Each page still brings its own column widths, spacing and
 * tone via `className` — these don't inject default visual styling, so adopting them on an existing
 * table is a markup-only change, not a redesign. What they standardise is structure and accessibility:
 * one scrollable wrapper, one place column headers get `scope="col"` by default, and (from Phase 2) one
 * place a table's rows learn to reflow into cards below `md` instead of only scrolling sideways.
 */

/**
 * Wraps a table so it scrolls horizontally instead of breaking the layout on a narrow screen, and
 * keeps that scroll region reachable from the keyboard (axe `scrollable-region-focusable`). `label`
 * is the table's name for screen reader users tabbing to the region ("Add-ons and renewal dates").
 */
export function TableScroll({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cx("overflow-x-auto", className)} role="region" tabIndex={0} aria-label={label}>
      {children}
    </div>
  );
}

export function Table({ className, ...props }: TableHTMLAttributes<HTMLTableElement>) {
  return <table className={cx(className)} {...props} />;
}

export function TableHead({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return <thead className={cx(className)} {...props} />;
}

export function TableBody({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody className={cx(className)} {...props} />;
}

export function TableRow({ className, ...props }: HTMLAttributes<HTMLTableRowElement>) {
  return <tr className={cx(className)} {...props} />;
}

/** A column header; `scope="col"` by default (pass `scope="row"` or `"colgroup"` for a row/group header). */
export function TableHeaderCell({
  scope = "col",
  className,
  ...props
}: ThHTMLAttributes<HTMLTableCellElement>) {
  return <th scope={scope} className={cx(className)} {...props} />;
}

export function TableCell({ className, ...props }: TdHTMLAttributes<HTMLTableCellElement>) {
  return <td className={cx(className)} {...props} />;
}
